import type { FeedMediaItem } from '~/components/PropertyReelsFeed/types';
import type { Property, ShortVideoProperty } from '~/core/entities/property';

/** Card só foto (`includeImagesEvery`): URL vem da galeria mapeada em `images`, não de `thumbnailUrl` do anúncio (ausente sem vídeo). */
function firstImageWatermarkUrl(property: ShortVideoProperty): string | null {
	const url = property.images?.find(img =>
		Boolean(img.watermarkUrl?.trim())
	)?.watermarkUrl;
	return url?.trim() ?? null;
}

export function propertyImages(property: {
	images?: ShortVideoProperty['images'];
}): string[] {
	return (property.images ?? [])
		.map(img => img.watermarkUrl?.trim() ?? '')
		.filter(Boolean)
		.slice(0, 10);
}

export function mapPropertyToFeedItem(
	property: ShortVideoProperty
): FeedMediaItem<ShortVideoProperty> | null {
	const short = property.featuredVideos?.[0];
	if (short) {
		return {
			kind: 'video',
			property,
			videoUrl: short.url,
			thumbnailUrl: short.thumbnail ?? property.thumbnailUrl ?? null,
			videoWidth: short.width,
			videoHeight: short.height,
			...(short.prefetchVariants?.length
				? { prefetchVariants: short.prefetchVariants }
				: {})
		};
	}

	const images = propertyImages(property);
	if (!images.length) return null;

	return {
		kind: 'fotos',
		property,
		videoUrl: null,
		thumbnailUrl: images[0] ?? null,
		images,
		videoWidth: undefined,
		videoHeight: undefined
	};
}

/** Compatibilidade para consumidores que ainda usam o nome anterior. */
export const mapPropertyToShortVideo = mapPropertyToFeedItem;

/** Normaliza o payload completo da PDP para o mesmo item leve usado pelo feed. */
export function toShortVideoProperty(property: Property): ShortVideoProperty {
	return {
		id: property.id,
		slug: property.slug,
		commercialId: property.commercialId,
		ad: {
			title: property.ad.title,
			transactionType: property.ad.transactionType
		},
		thumbnailUrl: property.thumbnailUrl,
		askingPrice: property.askingPrice,
		rentPrice: property.rentPrice,
		area: property.area,
		bedrooms: property.bedrooms,
		suites: property.suites,
		parkingSpots: property.parkingSpots,
		region: property.region,
		city: property.city,
		state: property.state,
		featuredVideos: property.featuredVideos,
		images: property.images,
		videoView: property.videoView,
		propertyType: property.propertyType,
		agent: {
			id: property.agent.id,
			slug: property.agent.slug ?? null,
			name: property.agent.name,
			displayName: property.agent.display_name ?? null,
			profilePictureUrl:
				property.agent.profilePicture?.croppedUrl ??
				property.agent.profilePicture?.url ??
				null
		},
		company: {
			name: property.company.name,
			logoUrl: property.company.smallLogo ?? property.company.largeLogo ?? null
		},
		isExclusive: property.isExclusive,
		openHouses: property.openHouses
	};
}
